import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';import { ChevronLeft, ChevronRight, Clock, Calendar, Newspaper, BookOpen, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import { SiteHeader } from './ServicesIndex';
import SiteFooter from '../components/SiteFooter';
import useSeo from '../hooks/useSeo';
import {
  BLOG_CATEGORIES,
  BLOG_ARTICLES,
} from '../data/blogArticles';

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

const accentByCategory = { termite: 'from-amber-600 to-orange-700', cockroach: 'from-rose-600 to-red-800', rodent: 'from-slate-600 to-slate-800', mosquito: 'from-sky-600 to-blue-800', bedbug: 'from-fuchsia-600 to-purple-800', ant: 'from-emerald-600 to-teal-800', news: 'from-blue-700 to-slate-900', general: 'from-teal-600 to-cyan-800' };
const publicArticle = (post) => ({ ...post, publishedOn: post.published_at, readMinutes: post.read_minutes || 1, heroAccent: accentByCategory[post.category] || accentByCategory.general, dateline: post.category_label || post.category, featuredImage: post.featured_image_url ? `${process.env.REACT_APP_BACKEND_URL}${post.featured_image_url}` : null });

// ─────────────────────────────────────────────────────────────
// Newspaper-style hero card for "Current News" articles
// ─────────────────────────────────────────────────────────────
function NewsCard({ article, featured = false }) {
  return (
    <Link
      to={`/blog/${article.slug}`}
      data-testid={`news-card-${article.slug}`}
      className={`group relative block overflow-hidden rounded-xl border-2 border-amber-900/30 bg-[#fbf6ec] shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5 ${featured ? 'md:col-span-2' : ''}`}
      style={{
        backgroundImage:
          'repeating-linear-gradient(0deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 1px, transparent 1px, transparent 4px)',
      }}
    >
      {/* Newspaper top stripe */}
      <div className={`relative h-32 sm:h-36 md:h-40 bg-gradient-to-br ${article.heroAccent} text-white px-5 py-3 flex flex-col justify-between`} style={article.featuredImage ? { backgroundImage: `linear-gradient(rgba(8,47,73,.42), rgba(8,47,73,.72)), url(${article.featuredImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] font-bold">
          <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-2 py-1 rounded">
            <Newspaper size={12} /> Current News
          </span>
          <span className="opacity-90">{article.dateline || 'EDITORIAL'}</span>
        </div>
        <div className="text-2xl font-black tracking-tight leading-tight" style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif' }}>
          THE PEST CONTROL TIMES
        </div>
      </div>

      <div className="px-5 py-5">
        <div className="flex items-center gap-3 text-[11px] text-amber-900/70 mb-2 font-semibold">
          <span className="inline-flex items-center gap-1"><Calendar size={11} />{formatDate(article.publishedOn)}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Clock size={11} />{article.readMinutes} min read</span>
        </div>
        <h3
          className="text-lg sm:text-xl font-bold text-stone-900 leading-snug mb-2 group-hover:text-brand-blue transition-colors"
          style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif' }}
        >
          {article.title}
        </h3>
        <p className="text-[13px] text-stone-700 leading-relaxed line-clamp-3">
          {article.excerpt}
        </p>
        <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold text-amber-800 uppercase tracking-wider">
          Read full report <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────
// Standard guide-style article card
// ─────────────────────────────────────────────────────────────
function GuideCard({ article }) {
  return (
    <Link
      to={`/blog/${article.slug}`}
      data-testid={`guide-card-${article.slug}`}
      className="group block overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
    >
      <div className={`h-36 bg-gradient-to-br ${article.heroAccent} relative overflow-hidden`} style={article.featuredImage ? { backgroundImage: `linear-gradient(rgba(15,23,42,.2), rgba(15,23,42,.64)), url(${article.featuredImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.3) 0%, transparent 40%)',
        }} />
        <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold bg-white/15 backdrop-blur-sm px-2 py-1 rounded">
            <BookOpen size={11} className="inline mr-1" /> Guide
          </span>
          <span className="text-[10px] font-semibold opacity-90">{article.readMinutes} min</span>
        </div>
      </div>
      <div className="px-5 py-4">
        <div className="text-[11px] text-slate-500 font-semibold mb-1.5 inline-flex items-center gap-1.5">
          <Calendar size={11} /> {formatDate(article.publishedOn)}
        </div>
        <h3 className="text-base font-bold text-slate-900 leading-snug mb-2 group-hover:text-brand-green transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-[13px] text-slate-600 leading-relaxed line-clamp-3">
          {article.excerpt}
        </p>
        <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold text-brand-green">
          Read article <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────
// Horizontal scrollable category tabs (with article counts)
// ─────────────────────────────────────────────────────────────
function CategoryTabs({ activeId, onChange, counts }) {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  // Scroll the active tab into view when changed
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const activeBtn = el.querySelector(`[data-tab-id="${activeId}"]`);
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeId]);

  const scrollBy = (delta) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className="relative" data-testid="blog-category-tabs">
      {/* Left arrow */}
      <button
        type="button"
        aria-label="Scroll categories left"
        onClick={() => scrollBy(-260)}
        className={`hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-lg border border-slate-200 items-center justify-center text-slate-600 hover:text-brand-green hover:border-brand-green transition-all ${canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        data-testid="blog-tabs-scroll-left"
      >
        <ChevronLeft size={18} />
      </button>

      {/* Scrollable tab strip */}
      <div
        ref={scrollerRef}
        className="flex items-center gap-2 sm:gap-2.5 overflow-x-auto scroll-smooth py-2 sm:px-10 px-2 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {BLOG_CATEGORIES.map((cat) => {
          const isActive = cat.id === activeId;
          const count = counts[cat.id] ?? 0;
          return (
            <button
              key={cat.id}
              data-tab-id={cat.id}
              type="button"
              onClick={() => onChange(cat.id)}
              data-testid={`blog-tab-${cat.id}`}
              className={`group shrink-0 inline-flex items-center gap-2 h-10 sm:h-11 pl-4 pr-1.5 sm:pl-5 sm:pr-2 rounded-full text-sm font-semibold tracking-tight transition-all duration-200 border ${
                isActive
                  ? 'bg-brand-green text-white border-brand-green shadow-md scale-[1.02]'
                  : count === 0
                  ? 'bg-slate-50 text-slate-400 border-slate-200 hover:text-slate-600 hover:bg-white'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-brand-green hover:text-brand-green hover:shadow-sm'
              }`}
            >
              <span className="whitespace-nowrap">{cat.label}</span>
              <span
                className={`inline-flex items-center justify-center min-w-[26px] h-7 px-2 rounded-full text-[11px] font-bold ${
                  isActive
                    ? 'bg-white/25 text-white'
                    : count === 0
                    ? 'bg-slate-200 text-slate-500'
                    : 'bg-brand-green/10 text-brand-green'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Right arrow */}
      <button
        type="button"
        aria-label="Scroll categories right"
        onClick={() => scrollBy(260)}
        className={`hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-lg border border-slate-200 items-center justify-center text-slate-600 hover:text-brand-green hover:border-brand-green transition-all ${canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        data-testid="blog-tabs-scroll-right"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Empty-state for categories with zero articles
// ─────────────────────────────────────────────────────────────
function EmptyState({ categoryLabel }) {
  return (
    <div
      className="rounded-2xl border-2 border-dashed border-slate-200 bg-white py-16 px-6 text-center"
      data-testid="blog-empty-state"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
        <Sparkles size={22} />
      </div>
      <h3 className="text-lg font-bold text-slate-700 mb-1.5">
        No {categoryLabel} articles yet
      </h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto">
        Our editorial team is preparing fresh content for this section. In the meantime, browse our latest current news or pest control guides.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Blog Index Page
// ─────────────────────────────────────────────────────────────
export default function BlogIndex({ onOpenCalculator, onOpenAuth }) {
  useSeo(
    'Blog & News',
    'Fresh pest control news, expert guides and FAQs from The Indian Pest Control — termites, cockroaches, rodents, mosquitoes and more.'
  );

  const [activeCategory, setActiveCategory] = useState('all');
  const [cmsArticles, setCmsArticles] = useState([]);
  useEffect(() => { let active = true; axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/blog/posts`).then((res) => { if (active) setCmsArticles((res.data.items || []).map(publicArticle)); }).catch(() => {}); return () => { active = false; }; }, []);
  const allArticles = useMemo(() => [...cmsArticles, ...BLOG_ARTICLES], [cmsArticles]);
  const counts = useMemo(() => allArticles.reduce((acc, article) => ({ ...acc, all: (acc.all || 0) + 1, [article.category]: (acc[article.category] || 0) + 1 }), { all: 0 }), [allArticles]);
  const articles = useMemo(() => activeCategory === 'all' ? allArticles : allArticles.filter((article) => article.category === activeCategory || (activeCategory === 'news' && article.type === 'news')), [activeCategory, allArticles]);

  // Push GTM dataLayer event on every category tab change so GTM-5FXLBKGC
  // can build category-specific audiences (e.g., users who viewed "Termites").
  useEffect(() => {
    try {
      const cat = BLOG_CATEGORIES.find((c) => c.id === activeCategory);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'blog_category_view',
        blog_category_id: activeCategory,
        blog_category_label: cat?.label || activeCategory,
        page_location: window.location.href,
        page_path: window.location.pathname + window.location.search,
      });
    } catch (e) { /* noop */ }
  }, [activeCategory]);

  // Split out news vs guides only on the "all" view to feature news at the top
  const showAllView = activeCategory === 'all';
  const showNewsView = activeCategory === 'news';
  const newsArticles = useMemo(
    () => articles.filter((a) => a.type === 'news'),
    [articles]
  );
  const guideArticles = useMemo(
    () => articles.filter((a) => a.type !== 'news'),
    [articles]
  );

  const activeCatLabel = BLOG_CATEGORIES.find((c) => c.id === activeCategory)?.label || 'Articles';

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: 'Poppins, Inter, system-ui, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} onOpenAuth={onOpenAuth} />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-blue via-blue-800 to-slate-900 text-white">
        <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-brand-green/30 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 py-12 sm:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold tracking-wide backdrop-blur-sm">
            <Newspaper size={14} className="text-brand-green-light" />
            Pest Insights · Current News · Expert Guides
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
            Blog &amp; News
          </h1>
          <p className="mt-3 text-sm sm:text-base text-blue-100 max-w-2xl">
            Fresh field reports, deep-dive guides and honest FAQs from the team behind The Indian Pest Control. Browse the latest current news at the top, then explore pest-specific articles using the tabs below.
          </p>
        </div>
      </section>

      {/* ── STICKY CATEGORY TABS ─────────────────────────────── */}
      <div className="sticky top-[88px] sm:top-[100px] z-30 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <CategoryTabs activeId={activeCategory} onChange={setActiveCategory} counts={counts} />
        </div>
      </div>

      {/* ── CONTENT ──────────────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
        {/* News-styled section (always shown on All and on News) */}
        {(showAllView || showNewsView) && newsArticles.length > 0 && (
          <section className="mb-12" data-testid="blog-news-section">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-stone-900 inline-flex items-center gap-2" style={{ fontFamily: '"Playfair Display", serif' }}>
                  <Newspaper size={22} className="text-amber-700" /> Current News
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">Fresh field reporting from our pest control teams</p>
              </div>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-700">
                {newsArticles.length} stories
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {newsArticles.map((a, i) => (
                <NewsCard key={a.slug} article={a} featured={i === 0} />
              ))}
            </div>
          </section>
        )}

        {/* Guide-card section */}
        {(!showNewsView) && guideArticles.length > 0 && (
          <section data-testid="blog-guides-section">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 inline-flex items-center gap-2" style={{ fontFamily: '"Playfair Display", serif' }}>
                  <BookOpen size={22} className="text-brand-green" />
                  {showAllView ? 'Pest Control Guides & Articles' : activeCatLabel}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {showAllView
                    ? 'Evergreen, expert-written guides for every household pest'
                    : `Everything we have published on ${activeCatLabel.toLowerCase()}`}
                </p>
              </div>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-green">
                {guideArticles.length} {guideArticles.length === 1 ? 'article' : 'articles'}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {guideArticles.map((a) => (
                <GuideCard key={a.slug} article={a} />
              ))}
            </div>
          </section>
        )}

        {/* Empty state */}
        {articles.length === 0 && <EmptyState categoryLabel={activeCatLabel} />}
      </main>

      <SiteFooter />
    </div>
  );
}
